import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Sun, Moon, AlertTriangle, MessageSquare, Edit, ArrowUpRight, Clock } from 'lucide-react';
import { mockProjects } from '../../mock/data';

const EscalationsList = () => {
  const navigate = useNavigate();
  const [darkMode, setDarkMode] = useState(true);

  const theme = {
    bg: darkMode ? 'bg-[#0a0a0f]' : 'bg-slate-50',
    card: darkMode ? 'bg-[#12121a]' : 'bg-white',
    text: darkMode ? 'text-white' : 'text-slate-900',
    textMuted: darkMode ? 'text-slate-400' : 'text-slate-500',
    border: darkMode ? 'border-white/5' : 'border-slate-200',
  };

  // Get escalated projects
  const escalatedProjects = mockProjects.filter(p => p.isEscalated);

  return (
    <div className={`min-h-screen ${theme.bg} ${theme.text}`}>
      {/* Ambient glow */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-red-500/10 rounded-full blur-[100px]" />
      </div>

      {/* Header */}
      <header className={`sticky top-0 z-40 ${theme.card}/80 backdrop-blur-xl border-b ${theme.border}`}>
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => navigate('/mgmt-app')}
              className="p-2 rounded-xl hover:bg-white/5"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-400" />
              <h1 className="font-semibold">Escalations</h1>
            </div>
          </div>
          <button 
            onClick={() => setDarkMode(!darkMode)}
            className="p-2 rounded-xl hover:bg-white/5"
          >
            {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </button>
        </div>
      </header>

      {/* Content */}
      <main className="relative z-10 p-4 space-y-3">
        {escalatedProjects.length === 0 ? (
          <div className={`${theme.card} rounded-2xl p-8 border ${theme.border} text-center`}>
            <div className="w-16 h-16 rounded-2xl bg-emerald-500/10 flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-8 h-8 text-emerald-400" />
            </div>
            <h3 className="font-semibold mb-1">All Clear!</h3>
            <p className={theme.textMuted}>No escalations at the moment</p>
          </div>
        ) : (
          escalatedProjects.map((project) => (
            <div 
              key={project.id} 
              className={`${theme.card} rounded-2xl border ${theme.border} overflow-hidden`}
            >
              {/* Urgency indicator */}
              <div className="h-1 bg-gradient-to-r from-red-500 to-orange-500" />
              
              <div className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-semibold">{project.name}</h3>
                    <p className={`text-sm ${theme.textMuted}`}>{project.company}</p>
                  </div>
                  <span className="px-3 py-1 rounded-full bg-red-500/10 text-red-400 text-xs font-medium border border-red-500/20">
                    L1
                  </span>
                </div>

                {/* Escalation details */}
                <div className={`p-3 rounded-xl ${darkMode ? 'bg-white/5' : 'bg-slate-50'} mb-4`}>
                  <p className={`text-sm ${theme.textMuted}`}>
                    <span className="text-red-400 font-medium">Reason: </span>
                    {project.escalationReason || 'Needs immediate attention'}
                  </p>
                </div>

                {/* Meta info */}
                <div className={`flex items-center gap-4 text-xs ${theme.textMuted} mb-4`}>
                  <div className="flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5" />
                    <span>Dec 2025</span>
                  </div>
                  <span>•</span>
                  <span>To: <span className="text-red-400">{project.escalatedTo || 'Chairman'}</span></span>
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  <button className="flex-1 py-2.5 rounded-xl bg-blue-500/10 text-blue-400 text-sm font-medium flex items-center justify-center gap-2 hover:bg-blue-500/20 transition-colors">
                    <Edit className="w-4 h-4" />
                    Edit
                  </button>
                  <button className="flex-1 py-2.5 rounded-xl bg-violet-500/10 text-violet-400 text-sm font-medium flex items-center justify-center gap-2 hover:bg-violet-500/20 transition-colors">
                    <MessageSquare className="w-4 h-4" />
                    Comment
                  </button>
                  <button className="flex-1 py-2.5 rounded-xl bg-fuchsia-500/10 text-fuchsia-400 text-sm font-medium flex items-center justify-center gap-2 hover:bg-fuchsia-500/20 transition-colors">
                    <ArrowUpRight className="w-4 h-4" />
                    Escalate
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </main>
    </div>
  );
};

export default EscalationsList;
